/**
 * AddToStashtButton - Overlay button for thumbnail grids
 * Shows: Add → Adding → Added → hides when saved
 */

import { cn } from "@/lib/utils";
import { useAddToStasht } from "@/hooks/useAddToStasht";
import { AddToStashtPill } from "@/components/shared/AddToStashtPill";

export interface AddToStashtButtonProps {
  postId: string;
  /** Whether the post is already saved (from server) */
  isSaved?: boolean;
  origin?: "discover" | "public_search";
  className?: string;
  /** Auth gate callback — when set, gates the save action for anonymous users */
  requireAuth?: (actionType: string, targetId: string, callback: () => void) => void;
}

export function AddToStashtButton({
  postId,
  isSaved: isSavedProp = false,
  className,
  requireAuth,
  origin,
}: AddToStashtButtonProps) {
  const { state, save, hasSaved } = useAddToStasht({ postId, isSaved: isSavedProp, origin });

  // Hide when saved and not showing transient state
  const effectivelySaved = isSavedProp || hasSaved;
  if (effectivelySaved && state === "idle") return null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (requireAuth) {
      requireAuth("save", postId, () => save());
    } else {
      save();
    }
  };

  const pillClassName = cn("absolute top-3 right-3 z-10", className);

  if (state === "success") {
    return (
      <AddToStashtPill state="success" label="Added" compact className={pillClassName} />
    );
  }

  if (state === "saving") {
    return (
      <AddToStashtPill state="saving" label="Adding" compact className={pillClassName} />
    );
  }

  return (
    <AddToStashtPill
      state="idle"
      label="+ Add"
      compact
      onClick={handleClick}
      className={pillClassName}
    />
  );
}

AddToStashtButton.displayName = "AddToStashtButton";
